import { Box, Checkbox, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys } from "../../../../api/queryKeys";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import { IntegrationsMapping } from "../IntegrationsMapping";
import OneTrustConnectForm from "./OneTrustConnectForm";

import add_client_credentials from "./images/add_client_credentials.png";
import client_credentials from "./images/client_credentials.png";
import client_credentials_details from "./images/client_credentials_details.png";
import client_credentials_next from "./images/client_credentials_next.png";
import client_credentials_scopes from "./images/client_credentials_scopes.png";

const INTEGRATION_CODE_NAME = "onetrust";

const OneTrustIntegration = () => {
  const colors = useColors();
  const integration = IntegrationsMapping[INTEGRATION_CODE_NAME];

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    integration.codeName
  );
  const [loading, setLoading] = useState(false);
  const { staticIPs: allowlistIps } = useStaticIPs();
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerOneTrustIntegration } = useAPI();

  const onSubmit = (values) => {
    setFormValues(values);
    setLoading(true);
    registerOneTrustIntegration(
      values.credentials.client_id,
      values.credentials.client_secret,
      values.credentials.tenant_url
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integration.displayName} integration successful`);
        setFormValues(null);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? `Once integrated, Breeze Security will connect to your ${integration.displayName} environment to manage data privacy and governance.`
            : ""
        }
        image={logos.OneTrustLogo}
        name={integration.displayName}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>
                {integration.displayName} is integrated!
              </Typography>
            </Box>
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep
                  title={`Access ${integration.displayName} Client Credentials Settings`}
                >
                  <Typography variant={"body2"}>
                    Login to your {integration.displayName} account with Admin
                    privileges.
                  </Typography>
                  <Typography variant={"body2"}>
                    Click the gear icon in the upper right corner to access
                    Global Settings.
                  </Typography>
                  <Typography variant={"body2"}>
                    Click <b>Access Management {">"} Client Credentials</b> on
                    the left sidebar.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: client_credentials,
                        altext: "Client Credentials Settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Create new Client Credentials">
                  <Typography variant={"body2"}>
                    Click the <b>Add</b> button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: add_client_credentials,
                        altext: "Add new Client Credentials",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    1. In the <b>Name</b> field, enter a name for the client
                    credential.
                  </Typography>
                  <Typography variant={"body2"}>
                    2. <b>Optional</b>: In the <b>Description</b> field, enter a
                    description for the client credential.
                  </Typography>
                  <Typography variant={"body2"}>
                    3. In the <b>Access token lifetime</b> field, select the
                    amount of time that should pass before the access token
                    expires. <b>(Recommended: 1 Hour)</b>
                  </Typography>
                  <Typography variant={"body2"}>
                    4. <b>Optional:</b> Turn on the <b>Restrict IP addresses</b>{" "}
                    setting to restrict incoming communication to specific IP
                    addresses. If this setting is enabled, enter an IP address
                    in the Restrict IP addresses field that appears. You can
                    enter multiple IP addresses by clicking the Add icon and
                    entering an additional IP address in the field that appears.
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                  >
                    IP Addresses to Allowlist:
                  </Typography>
                  {allowlistIps.map((ip, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        margin: 0,
                        padding: "4px 0",
                        marginLeft: "20px",
                      }}
                    >
                      <Typography variant="body1">{ip}</Typography>
                      <CustomCopyButton
                        copyTitle={"IP Address"}
                        copyText={ip}
                      />
                    </Box>
                  ))}
                  <Typography variant={"body2"}>
                    Click the <b>Next</b> button to continue.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: client_credentials_next,
                        altext: "Client Credentials Next Step",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Configure Client Credential Scopes">
                  <Typography variant={"body2"}>
                    Select the following scopes:
                  </Typography>
                  <Box
                    sx={{
                      width: "900px",
                      backgroundColor: colors.grey_200,
                      padding: "16px",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color={colors.primary_darker}
                    >
                      Scopes
                    </Typography>
                    <Typography variant="body2">
                      Check the box for each completed scope to track the
                      progress
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>ITRM</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>INVENTORY_READ</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>OBJECT_MANAGER_READ</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>ASSESSMENT_READ</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox />
                      <Typography>VRM_READ</Typography>
                    </Box>
                  </Box>

                  <Typography variant={"body2"}>
                    Click the <b>Create</b> button to continue.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: client_credentials_scopes,
                        altext: "Configure Client Credential Scopes",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep
                  title={`Enter your ${integration.displayName} Details`}
                >
                  <Typography variant={"body2"}>
                    Now you will see your Client Credentials, you need to copy
                    the Client ID and Client Secret and paste them into the form
                    below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: client_credentials_details,
                        altext: `Copy your ${integration.displayName} Client Credentials`,
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    You will need the following information:
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant={"body2"}>
                        Your {integration.displayName} Tenant URL (e.g.,
                        https://yourcompany.my.onetrust.com)
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>The Client ID</Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        The Client Secret
                      </Typography>
                    </li>
                  </ul>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    Fill out the form below to complete the integration.
                  </Typography>
                  <OneTrustConnectForm
                    onSubmit={onSubmit}
                    guideMode={guideMode}
                    savedFormValues={formValues}
                  />
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default OneTrustIntegration;
